// ABOUTME: One task at a time per key, with the queued request merged and an optional minimum gap
// ABOUTME: between starts — the single answer to "these must not overlap, and none may be lost".
import { createLogger } from "@/model/logger"

const logger = createLogger("Realtime")

/**
 * A task that has not settled after this long is treated as gone, so a hung
 * request cannot block its key until a reload. Inherited from the coalescer
 * this replaced, and it still earns its place: every task here is a network
 * request, and a request that never settles is exactly what a flaky mobile
 * link produces.
 */
export const RUN_SERIAL_STALL_MS = 60_000

export interface RunSerialOptions<T> {
  /** Combines two queued requests. Default: the later one wins. */
  merge?: (queued: T, incoming: T) => T
  /** Minimum gap between two starts for this key. The first call is immediate. */
  minIntervalMs?: number
  /**
   * How long a task may run before the key is handed to whatever is queued.
   * Default RUN_SERIAL_STALL_MS; `Infinity` never hands it over.
   *
   * The takeover is the one case where two tasks of a key overlap, and it is
   * only safe for a caller that can drop a superseded answer before it writes
   * (`isStale` below). A caller whose answer is published by something else —
   * Apollo's own hook, which surfaces whichever response arrives and cannot say
   * whose it is — must forbid it, or the older answer can land last. Blocking
   * is bounded there anyway: the query is torn down when the selection changes
   * or the screen goes away, and its request aborts with it.
   */
  stallMs?: number
}

/**
 * What became of one run. `aborted` is its own case on purpose: a query torn
 * down by navigation rejects with AbortError, and reporting that as a failure
 * showed the user a server error for walking away from the screen.
 */
export type RunResult<R> = { status: "done"; value: R } | { status: "failed"; error: unknown } | { status: "aborted" }

interface Queued<T, R> {
  payload: T
  run: (payload: T, isStale: () => boolean) => Promise<R>
  resolve: (result: RunResult<R>) => void
  promise: Promise<RunResult<R>>
}

interface Entry<T, R = unknown> {
  /** When the running task started, null when nothing runs. */
  startedAt: number | null
  /** Identity of the running task, so a late straggler cannot release its successor. */
  running: symbol | null
  lastStartAt: number
  queued: Queued<T, R> | null
  timer: ReturnType<typeof setTimeout> | null
  merge?: (queued: T, incoming: T) => T
  minIntervalMs: number
  stallMs: number
}

// Keys are a handful of fixed strings plus one per chunk of the visible
// selection, so the map is bounded by what is on screen. Entries outlive their
// task on purpose: `lastStartAt` is what makes the minimum gap hold across calls.
const entries = new Map<string, Entry<unknown>>()

// A query torn down mid-request rejects with AbortError — nothing to run any more.
const isAbort = (error: unknown): boolean => error instanceof Error && error.name === "AbortError"

/** Nothing running, nothing queued, no timer pending — the entry holds no state worth keeping. */
function isIdle<T, R>(entry: Entry<T, R>): boolean {
  return entry.queued === null && entry.timer === null && entry.startedAt === null
}

function maybeStart<T, R>(key: string, entry: Entry<T, R>): void {
  if (entry.queued === null || entry.timer !== null) return
  const now = Date.now()
  if (entry.startedAt !== null) {
    // Come back when the running task would count as stalled; a task that
    // finishes first clears this timer on its way out. A key that never hands
    // over just waits: the running task calls back here on its way out.
    if (!Number.isFinite(entry.stallMs)) return
    const remaining = entry.stallMs - (now - entry.startedAt)
    if (remaining > 0) {
      entry.timer = setTimeout(() => {
        entry.timer = null
        maybeStart(key, entry)
      }, remaining)
      return
    }
  }
  const wait = Math.max(0, entry.minIntervalMs - (now - entry.lastStartAt))
  if (wait > 0) {
    entry.timer = setTimeout(() => {
      entry.timer = null
      maybeStart(key, entry)
    }, wait)
    return
  }
  const job = entry.queued
  const token = Symbol("run")
  entry.queued = null
  entry.startedAt = now
  entry.lastStartAt = now
  entry.running = token
  void job
    // A task that outlives the stall timeout keeps running with no way to stop
    // it, so it is told instead: `isStale` turns true the moment the key moves
    // on, and a task that writes anywhere must check it before it does.
    .run(job.payload, () => entry.running !== token)
    .then(
      (value): RunResult<R> => ({ status: "done", value }),
      (error: unknown): RunResult<R> => {
        if (isAbort(error)) return { status: "aborted" }
        logger.warn("Serialized task failed", { key, error })
        return { status: "failed", error }
      },
    )
    .then((result) => {
      // A task that outlived its stall timeout no longer owns the key: another
      // run took it over, and clearing the entry here would start a third task
      // alongside that one. Its own waiters are still released.
      if (entry.running !== token) {
        job.resolve(result)
        return
      }
      entry.startedAt = null
      entry.running = null
      if (entry.timer !== null) {
        clearTimeout(entry.timer)
        entry.timer = null
      }
      job.resolve(result)
      maybeStart(key, entry)
      // Nothing left to do for this key: drop it rather than keep one entry per
      // athlete a coach has ever opened. A key with a minimum gap keeps its
      // entry — `lastStartAt` is what makes that gap hold across calls.
      if (isIdle(entry) && entry.minIntervalMs === 0) entries.delete(key)
    })
}

/**
 * Runs `task` for `key`, never alongside another task of the same key. A
 * request arriving while one runs is queued rather than dropped; two queued
 * requests combine through `merge`. The returned promise settles with what
 * became of the run that carried this request — done, failed or aborted — and
 * never rejects, so a background caller cannot produce an unhandled rejection.
 *
 * `task` is handed an `isStale()` alongside its payload. It answers false while
 * this run still owns the key and true once a stall timeout has handed the key
 * to a successor — the one case where two tasks of a key really do overlap. A
 * task that writes shared state MUST check it before writing, or its older
 * answer lands on top of the newer one.
 */
export function runSerial<T, R = unknown>(
  key: string,
  payload: T,
  task: (payload: T, isStale: () => boolean) => Promise<R>,
  options: RunSerialOptions<T> = {},
): Promise<RunResult<R>> {
  const entry =
    (entries.get(key) as Entry<T, R> | undefined) ??
    ({
      startedAt: null,
      running: null,
      lastStartAt: Number.NEGATIVE_INFINITY,
      queued: null,
      timer: null,
      merge: undefined,
      minIntervalMs: 0,
      stallMs: RUN_SERIAL_STALL_MS,
    } as Entry<T, R>)
  // Options belong to the key, set when it is created. Re-applying them per
  // call would let any caller quietly change the overlap rules of a task that
  // is already running — including one that omits them entirely.
  if (!entries.has(key)) {
    entry.merge = options.merge
    entry.minIntervalMs = options.minIntervalMs ?? 0
    entry.stallMs = options.stallMs ?? RUN_SERIAL_STALL_MS
  }
  entries.set(key, entry as Entry<unknown>)

  if (entry.queued !== null) {
    entry.queued.payload = entry.merge ? entry.merge(entry.queued.payload, payload) : payload
    entry.queued.run = task
    return entry.queued.promise
  }
  let resolve!: (result: RunResult<R>) => void
  const promise = new Promise<RunResult<R>>((settle) => {
    resolve = settle
  })
  entry.queued = { payload, run: task, resolve, promise }
  maybeStart(key, entry)
  return promise
}

/** Test seam — the per-key state outlives a test file otherwise. */
export function resetRunSerialForTests(): void {
  for (const entry of entries.values()) {
    if (entry.timer !== null) clearTimeout(entry.timer)
  }
  entries.clear()
}
